import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { languageOptions } from '@constants/languages.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import {
  updateRequesterSchema,
  type UpdateRequesterDto,
  type UpdateRequesterPayload,
} from '@services/requesters/dtos/update.dto';
import { requesterService } from '@services/requesters/service';
import type { GetRequesterByIdOutput } from '@services/requesters/types/getById.type';
import type { UpdateRequesterOutput } from '@services/requesters/types/update.type';
import type { GetServiceRequestByIdOutput } from '@services/serviceRequests/types/getById.type';

function requesterQueryOptions(id: string) {
  return {
    queryKey: queryKeys.requesters.detail(id),
    queryFn: () => requesterService.getById(id),
    staleTime: EStaleTime.OneHour,
  };
}

export const Route = createFileRoute(
  '/_layout/(private)/requesters/$id/update',
)({
  component: RequesterUpdate,
  loader: async ({ context, params }) => {
    const options = requesterQueryOptions(params.id);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircle className="text-primary animate-spin" size={80} />
    </div>
  ),
  pendingMs: 300,
  pendingMinMs: 600,
  validateSearch: z.object({
    serviceRequestId: z.string().optional(),
  }),
});

function RequesterUpdate() {
  const { queryClient } = Route.useRouteContext();
  const { id } = Route.useParams();
  const { serviceRequestId } = Route.useSearch();

  const navigate = Route.useNavigate();

  const {
    data: { requester },
  } = useSuspenseQuery(requesterQueryOptions(id));

  const form = useForm<UpdateRequesterDto>({
    resolver: zodResolver(updateRequesterSchema),
    mode: 'onBlur',
    defaultValues: {
      email: requester.email,
      firstName: requester.firstName,
      lastName: requester.lastName,
      language: requester.language,
      phone: requester.phone,
      serviceAtHomeAddress: requester.serviceAtHomeAddress,
      address: {
        street: requester.address.street,
        number: requester.address.number,
        postcode: requester.address.postcode,
        city: requester.address.city,
      },
    },
  });

  const { isPending, mutate: updateRequester } = useMutation<
    UpdateRequesterOutput,
    unknown,
    UpdateRequesterPayload
  >({
    mutationFn: requesterService.update,
    onError: handleRequestError,
    onSuccess: async ({ requester: updatedRequester }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.serviceRequests.allDetail,
      });

      queryClient.setQueriesData<GetServiceRequestByIdOutput>(
        { queryKey: queryKeys.serviceRequests.allDetail },
        oldData => {
          if (
            !oldData ||
            oldData.serviceRequest.requester.id !== updatedRequester.id
          ) {
            return oldData;
          }

          return {
            serviceRequest: {
              ...oldData.serviceRequest,
              requester: {
                email: updatedRequester.email,
                firstName: updatedRequester.firstName,
                id: updatedRequester.id,
                language: updatedRequester.language,
                lastName: updatedRequester.lastName,
                phone: updatedRequester.phone,
              },
            },
          };
        },
      );

      await queryClient.cancelQueries({
        queryKey: queryKeys.requesters.detail(id),
      });

      queryClient.setQueryData<GetRequesterByIdOutput>(
        queryKeys.requesters.detail(id),
        oldData => {
          if (!oldData) {
            return oldData;
          }

          return {
            requester: updatedRequester,
          };
        },
      );

      navigate({
        to: serviceRequestId ? '/service-requests/$id' : '..',
        params: { id: serviceRequestId || requester.id },
        replace: true,
      });
      toast.success('Success', {
        description: 'Requester updated successfully.',
      });
    },
  });

  const isSameAsServiceRequest = useWatch({
    control: form.control,
    name: 'serviceAtHomeAddress',
  });

  const handleSubmit = form.handleSubmit(values => {
    updateRequester({
      id,
      address: !values.serviceAtHomeAddress ? values.address : undefined,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      language: values.language,
      phone: values.phone,
      serviceAtHomeAddress: values.serviceAtHomeAddress,
    });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle
        hasBackButton
        params={{ id: serviceRequestId || requester.id }}
        title="Update Requester"
        to={serviceRequestId ? '/service-requests/$id' : '..'}
      />

      <form className="space-y-2" onSubmit={handleSubmit}>
        <div className="grid h-fit grid-cols-3 items-start gap-2">
          <InputControlled label="First name" name="firstName" />
          <InputControlled label="Last name" name="lastName" />
          <InputControlled label="Email" name="email" type="email" />
          <InputControlled label="Phone" name="phone" variant="phone" />
          <InputControlled
            label="Language"
            name="language"
            options={languageOptions}
            variant="select"
          />
        </div>

        <div className="mt-3 grid space-y-2">
          <InputControlled
            name="serviceAtHomeAddress"
            title="Official address same as request address"
            variant="checkbox"
          />

          {!isSameAsServiceRequest && (
            <div className="grid grid-cols-2 gap-2">
              <InputControlled label="Street" name="address.street" />
              <InputControlled label="Number" name="address.number" />
              <InputControlled label="Postcode" name="address.postcode" />
              <InputControlled label="City" name="address.city" />
            </div>
          )}
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            params={{ id: serviceRequestId || requester.id }}
            rounded="lg"
            size="lg"
            to={serviceRequestId ? '/service-requests/$id' : '..'}
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
